import type { ReactNode } from "react";

/**
 * Links in a message, made clickable — without ever rendering markup.
 *
 * THE RULE THIS FILE EXISTS TO KEEP. Message content is somebody else's words.
 * A stranger writes it at a door and a model writes it back; neither is
 * trusted, and nothing in this product has ever put either through
 * `dangerouslySetInnerHTML`. So this does not "render HTML in messages": it
 * SPLITS a string on a URL pattern and returns React elements, where every
 * non-URL piece is still a text node React escapes. There is no path from a
 * message body to markup, and there must never be one.
 *
 * The pattern is deliberately narrow — `http://` or `https://` only. Bare
 * domains, `mailto:`, `javascript:` and anything else stay plain text: a
 * linkifier that guesses is a linkifier that eventually turns
 * `javascript:alert(1)` into an anchor.
 */
const URL_RE = /(https?:\/\/[^\s<>"']+)/g;

/** Trailing punctuation belongs to the sentence, not to the address. */
const TRAILING = /[),.;:!?]+$/;

export function linkify(text: string): ReactNode {
  const parts = text.split(URL_RE);
  if (parts.length === 1) return text;
  return parts.map((part, index) => {
    if (!/^https?:\/\//.test(part)) return part;
    const href = part.replace(TRAILING, "");
    const trail = part.slice(href.length);
    // A second guard behind the pattern: only these two schemes are ever an
    // anchor, whatever the regex let through.
    let safe = false;
    try {
      const parsed = new URL(href);
      safe = parsed.protocol === "http:" || parsed.protocol === "https:";
    } catch {
      safe = false;
    }
    if (!safe) return part;
    return (
      <span key={index}>
        <a
          href={href}
          target="_blank"
          rel="noreferrer noopener nofollow"
          className="underline underline-offset-2 focus-visible:outline-none focus-visible:ring-2
                     focus-visible:ring-focus/70"
        >
          {href.replace(/^https?:\/\//, "")}
        </a>
        {trail}
      </span>
    );
  });
}

/**
 * The files on a paste, if the paste carried any.
 *
 * A screenshot on the clipboard is the commonest attachment there is, and the
 * commonest way to lose one is to have no answer for Cmd-V.
 */
export function filesFromClipboard(data: DataTransfer | null): File[] {
  const out: File[] = [];
  const items = data?.items;
  if (!items) return out;
  for (const item of Array.from(items)) {
    if (item.kind !== "file") continue;
    const file = item.getAsFile();
    if (file) out.push(file);
  }
  return out;
}
